import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useActivateAccountWithRecaptchaMutation } from '../redux/features/api';
import { useRecaptcha } from './useRecaptcha';
import queryString from 'query-string';
import {
  trackActivationPageView,
  trackActivationClick,
  trackActivationSuccess,
  trackActivationError
} from '../helpers';

export const useAccountActivationWithRecaptcha = () => {
  const location = useLocation();
  const [activateAccount, { isLoading, isSuccess, isError, error }] =
    useActivateAccountWithRecaptchaMutation();
  const { executeRecaptcha } = useRecaptcha();
  const [token, setToken] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Extract token from URL query parameters
    const parsed = queryString.parse(location.search);
    if (parsed.token) {
      setToken(parsed.token);
      // Track page view with valid token
      trackActivationPageView(true);
    } else {
      // Track page view without valid token
      trackActivationPageView(false);
    }
  }, [location.search]);

  useEffect(() => {
    // Update error message and track errors
    if (isError && error) {
      const message =
        error?.data?.message || error?.message || 'Activation failed. Please try again.';
      setErrorMessage(message);

      // Determine error type for analytics
      let errorType = 'unknown_error';
      if (message.toLowerCase().includes('expired')) {
        errorType = 'expired_token';
      } else if (message.toLowerCase().includes('invalid')) {
        errorType = 'invalid_token';
      } else if (message.toLowerCase().includes('already activated')) {
        errorType = 'already_activated';
      }

      trackActivationError(errorType);
    }
  }, [isError, error]);

  useEffect(() => {
    // Track activation success
    if (isSuccess) {
      trackActivationSuccess();
    }
  }, [isSuccess]);

  const handleActivate = useCallback(async () => {
    if (!token) {
      setErrorMessage('Invalid activation token');
      trackActivationError('missing_token');
      return;
    }

    try {
      // Track activation button click
      trackActivationClick();

      // Get reCAPTCHA token
      const recaptchaToken = await executeRecaptcha('activate_account');

      // Call the activation API with reCAPTCHA
      await activateAccount({
        token,
        recaptchaToken
      }).unwrap();
    } catch (error) {
      console.error('Activation error:', error);
      const message =
        error?.data?.message || error?.message || 'Activation failed. Please try again.';
      setErrorMessage(message);
    }
  }, [token, activateAccount, executeRecaptcha]);

  return {
    handleActivate,
    isLoading,
    isSuccess,
    isError,
    errorMessage,
    token
  };
};

